import axiosInitializer from '@/api/axiosInitializer';
import { clearLocalStorage, clearCookies } from '@/store/profile/functions';
import azureAuth from '@/screens/login/azure/azureAuth';
import usersApi from '@/api/usersApi';
import loginUtil from '@/screens/login/loginUtil.js';
import remoteLogger from '@/api/remoteLoggerApi';
import { PDF_VIEWERS } from '@/common/constants';

const ROOT = { root: true };

const state = {
  customerRelations: {
    customer: '',
    customerFullName: '',
    tenants: [],
    tenant: '',
    tenantShortName: '',
    tenantName: '',
    tenantFullName: '',
  },
  previousUser: {},
  userWhenLoggingOut: null,
  user: {
    userName: '',
    firstName: '',
    lastName: '',
    groups: '',
    token: '',
    tokenExpiry: 0,
    refreshToken: '',
    rememberMe: false,
  },
  isAuthenticated: false,
  onPasswordChange: false,
  userProfile: {
    baseFontSize: 16,
    pdfViewer: 'pdfjs',
    displayPdfChangeViewer: false,
    displayPdfViewerHintMode: 'display',
    timeFormat: '24+hh:mm',
  },
};

const getters = {
  getUserProfile: (state) => {
    return state.userProfile;
  },
};

const mutations = {
  setUserWhenLoggingOut(state, user) {
    state.userWhenLoggingOut = user;
  },
  setCustomerRelations(state, { customerRelations }) {
    state.customerRelations = customerRelations;
  },

  clearUser(state) {
    console.debug('perform mutation profile/clearUser');
    state.previousUser = state.user;
    state.user = {};

    clearLocalStorage();
    clearCookies();
    // if (this.state.configs.features.azure != undefined) {
    //   signOut();
    // }

    state.isAuthenticated = false;
  },
  initUserProfile(state, tenantUserProfileDefaults) {
    let userProfile = JSON.parse(localStorage.getItem('userProfile')) || tenantUserProfileDefaults;
    // try {
    if (!userProfile.baseFontSize) userProfile.baseFontSize = 16;
    if (!userProfile.pdfViewer) userProfile.pdfViewer = 'pdfjs';
    if (!userProfile.displayPdfChangeViewer) userProfile.displayPdfChangeViewer = false;
    if (!userProfile.displayPdfViewerHintMode) userProfile.displayPdfViewerHintMode = 'display';
    if (!userProfile.timeFormat) userProfile.timeFormat = '24+hh:mm';
    localStorage.setItem('userProfile', JSON.stringify(userProfile));

    let r = document.querySelector(':root');
    r.style.setProperty('--base-font-size', `${userProfile.baseFontSize ? userProfile.baseFontSize : '16'}px`);
    //  store in state for reactivity reasons
    state.userProfile = userProfile;
  },
  /**
   * Sets the Pdf viewer option
   * @param state - injected state
   * @param pdfViewerName - one of pdfjs | pdfjsexpress | desktopembedded
   */
  setPdfViewer(state, pdfViewerName) {
    console.debug('setPdfViewer', pdfViewerName);
    if (!PDF_VIEWERS.includes(pdfViewerName)) {
      console.warn('The provided pdfViewerName is not valid:', pdfViewerName, 'Use one of ', PDF_VIEWERS);
    } else {
      state.userProfile.pdfViewer = pdfViewerName;
      localStorage.setItem('userProfile', JSON.stringify(state.userProfile));
    }
  },
  /**
   * Sets the flag to display the pdf viewer switcher
   * @param state - injected state
   * @param value - boolean
   */
  setDisplayPdfChangeViewer(state, value) {
    console.debug('setDisplayPdfChangeViewer', value);
    state.userProfile.displayPdfChangeViewer = value;
    localStorage.setItem('userProfile', JSON.stringify(state.userProfile));
  },
  /**
   * Sets the flag to display the pdf viewer hint mode: display || button || none
   * @param state - injected state
   * @param value - boolean
   */
  setDisplayPdfViewerHintMode(state, value) {
    console.debug('setDisplayPdfViewerHintMode', value);
    state.userProfile.displayPdfViewerHintMode = value;
    localStorage.setItem('userProfile', JSON.stringify(state.userProfile));
  },
  setUserProfile(state, userProfile) {
    localStorage.setItem('userProfile', JSON.stringify(userProfile));
    state.userProfile = userProfile;
  },
  setOnPasswordChange(state, onPasswordChange) {
    state.onPasswordChange = onPasswordChange;
  },
};

const actions = {
  setUserId({ state, dispatch }, userId) {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user) {
      user.userId = userId;
      localStorage.setItem('user', JSON.stringify(user, null, 2));
    }
    state.user.userId = userId;
  },

  setUser(
    { state, dispatch },
    { locale, token, refreshToken, userName, employeeNr, email, firstName, lastName, groups, rememberMe, expiresInSeconds, userId }
  ) {
    if (!locale) {
      locale = localStorage.getItem('locale');
    }
    console.debug('profile/setUser', lastName, userId, locale);
    let now = new Date();
    let tokenExpiry = new Date();
    tokenExpiry.setSeconds(now.getSeconds() + expiresInSeconds - 10);

    state.user = {
      userName,
      email,
      employeeNr,
      firstName,
      lastName,
      groups,
      token,
      tokenExpiry,
      refreshToken,
      rememberMe,
      userId,
    };
    axiosInitializer.onSuccessfulLogin(token);

    localStorage.setItem('locale', locale);
    localStorage.setItem('tokenExpiry', tokenExpiry);
    localStorage.setItem('user', JSON.stringify({ userName, employeeNr, email, firstName, lastName, groups, userId }));

    localStorage.setItem('customerRelations', JSON.stringify(state.customerRelations));
    dispatch('lifecycle/setAppStates', { authenticated: true, logMsg: 'User is authenticated' }, { root: true });
  },
  async login({ dispatch, state, rootState }, { tenant, username, password, token = '' }) {
    console.debug('-----login');
    let response;
    try {
      response = await usersApi.login({ tenant, username, password, token });
      if (response) console.debug('login response');
    } catch (err) {
      console.warn('login request failed', err);
      throw err;
    }
    let { expiresInSeconds, refreshToken, user } = response.data;
    token = response.data.token;

    let { locale, employeeNr, firstName, lastName, email, groups, cockpitAccessToken, userId } = user || {};
    if (response.status !== 200) {
      throw Error('Login failed with response.status=' + response.status);
    }
    if (username !== user.username) {
      throw Error(`Login failed because returned username (${user.username} is not the same as logged in ${username}`);
    }

    // first check if the user is a new user. In that case reset the app.
    let previousUser = state.previousUser;
    // console.debug('need to reset app because another user logs in? previousUser', JSON.stringify(previousUser, null, 2), 'username', username);
    if (previousUser) {
      let storedUser = previousUser;
      if (storedUser && storedUser.userName && storedUser.userName !== username) {
        let msg = 'need to reset app because another user logs in';
        console.debug(msg);
        await remoteLogger.cockpitLog({
          message: msg,
          addBrowserInfo: true,
          messageType: 'logout',
        });
        try {
          await dispatch('lifecycle/resetCaches', {}, { root: true });
          console.debug('application reset completed');
        } catch (err) {
          console.warn('Failed to resetCaches ignoring', err);
        }

        // however we need to perform login request once more to get the response into the application cache
        // This is done silently
        try {
          await usersApi.login({ tenant, username, password, token });
        } catch (err) {
          console.warn('silent login (for caching) failed', err);
        }

        // Reload app
        window.location.reload();
      }
    }

    let credentials = {
      token,
      refreshToken,
      expiresInSeconds,
      userName: username,
      employeeNr,
      firstName,
      lastName,
      email,
      groups,
      cockpitAccessToken,
      userId,
      locale,
      rememberMe: this.remember,
    };
    dispatch('profile/setUser', credentials, ROOT);
    let msg = 'Login performed successfully';
    await remoteLogger.cockpitLog({ message: msg, addBrowserInfo: true, messageType: 'login' });

    dispatch('lifecycle/setAppStates', { authenticated: true, msg }, ROOT);

    return credentials;
  },
  logout: async ({ rootState, state, commit, dispatch }, payload) => {
    let onPasswordChange = payload ? payload.onPasswordChange : false;
    let user = payload && payload.user ? payload.user : state.user;
    commit('setUserWhenLoggingOut', user);
    console.debug('perform action <<profile/logout>> user', user, 'onPasswordChange', onPasswordChange);
    commit('setOnPasswordChange', onPasswordChange);
    await remoteLogger.cockpitLog({ message: 'logout', addBrowserInfo: true, messageType: 'logout' });

    let cloudAuthProviderConfig = loginUtil.getCloudAuthProviderConfig(rootState.configs);

    let azureAccessToken = localStorage.getItem('azure.access-token');

    if (cloudAuthProviderConfig && azureAccessToken) {
      switch (cloudAuthProviderConfig.type) {
        case 'azure':
          console.debug('perform azure logout');
          await azureAuth.signOut(cloudAuthProviderConfig);

          // if manual login is enabled, we need to clear the user as well because otherwise we will
          // be automatically logged in again
          if (cloudAuthProviderConfig.manualLoginEnabled) {
            commit('clearUser');
            commit('hideNavigationDrawer', {}, { root: true });
          }

          // azureAuth.signOut(features.cloudAuthProviderConfig);
          // do not clear user as we cannot login in offline state otherwise: commit('clearUser');
          dispatch('lifecycle/setAppStates', { authenticated: false, configsLoaded: true }, { root: true });
          break;
      }
    } else {
      commit('clearUser');
      commit('hideNavigationDrawer', {}, { root: true });
      dispatch('lifecycle/setAppStates', { authenticated: false, configsLoaded: true }, { root: true });
    }
    dispatch('stopServerReachabilityCheck', {}, { root: true });
    commit('welcome/setShowWelcome', true, { root: true });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
